import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { LogOut } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Logo } from "@/components/brand/Logo";
import { OrdersTable } from "@/components/admin/OrdersTable";
import { ProductsTable } from "@/components/admin/ProductsTable";
import { ReviewsTable } from "@/components/admin/ReviewsTable";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { formatEUR } from "@/lib/format";
import { Seo } from "@/components/seo/Seo";
import { SEO } from "@/data/seo";

const Stat = ({ label, value, testid }) => (
  <div className="rounded-3xl border border-copper/20 bg-cream p-7">
    <p className="text-[13px] uppercase tracking-[0.2em] text-slate2">{label}</p>
    <p className="mt-3 font-display text-4xl text-navy" data-testid={testid}>{value}</p>
  </div>
);

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const orders = useQuery({ queryKey: ["admin-orders"], queryFn: async () => (await api.get("/admin/orders")).data });
  const products = useQuery({ queryKey: ["admin-products"], queryFn: async () => (await api.get("/admin/products")).data });
  const stats = orders.data?.stats;
  const productList = Array.isArray(products.data) ? products.data : [];
  const lowStock = productList.filter((p) => p.stock < 10).length;

  return (
    <div className="pb-24" data-testid="admin-dashboard">
      <Seo {...SEO.private} />
      <header className="border-b border-copper/20 bg-cream">
        <div className="container-pf flex h-20 items-center justify-between gap-4">
          <Link to="/" data-testid="admin-logo-link"><Logo /></Link>
          <div className="flex items-center gap-4">
            <span className="hidden text-[15px] text-slate2 sm:inline">{user?.email}</span>
            <button type="button" onClick={logout} data-testid="admin-logout" className="inline-flex h-11 items-center gap-2 rounded-full border border-navy/20 px-5 text-[15px] text-navy hover:bg-navy hover:text-ivory">
              <LogOut className="h-4 w-4" /> Esci
            </button>
          </div>
        </div>
      </header>
      <div className="container-pf pt-12">
        <p className="eyebrow">Pannello di amministrazione</p>
        <h1 className="mt-4 font-display text-5xl font-medium text-navy">Buongiorno.</h1>
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <Stat label="Ordini pagati" value={stats?.paid_count ?? 0} testid="stat-paid-count" />
          <Stat label="Da spedire" value={stats?.to_ship_count ?? 0} testid="stat-to-ship" />
          <Stat label="Incasso" value={formatEUR(stats?.revenue ?? 0)} testid="stat-revenue" />
          <Stat label="Prodotti sotto 10 pezzi" value={lowStock} testid="stat-low-stock" />
        </div>
        <Tabs defaultValue="orders" className="mt-12">
          <TabsList className="h-auto flex-wrap justify-start rounded-[1.75rem] bg-sand p-1.5">
            <TabsTrigger value="orders" data-testid="admin-tab-orders" className="h-11 rounded-full px-6 text-[15px] data-[state=active]:bg-navy data-[state=active]:text-ivory">Ordini</TabsTrigger>
            <TabsTrigger value="products" data-testid="admin-tab-products" className="h-11 rounded-full px-6 text-[15px] data-[state=active]:bg-navy data-[state=active]:text-ivory">Prodotti e prezzi</TabsTrigger>
            <TabsTrigger value="reviews" data-testid="admin-tab-reviews" className="h-11 rounded-full px-6 text-[15px] data-[state=active]:bg-navy data-[state=active]:text-ivory">Recensioni</TabsTrigger>
          </TabsList>
          <TabsContent value="orders" className="mt-8">
            {orders.isLoading ? <p className="text-slate2">Caricamento ordini…</p> : <OrdersTable orders={orders.data?.orders ?? []} />}
          </TabsContent>
          <TabsContent value="products" className="mt-8">
            {products.isLoading ? <p className="text-slate2">Caricamento prodotti…</p> : <ProductsTable products={productList} />}
          </TabsContent>
          <TabsContent value="reviews" className="mt-8">
            <ReviewsTable />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
